import { CommonModule } from '@angular/common';
import { Component, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ButtonDeleteContract } from '@app/features/contracts/button-delete-contract/button-delete-contract';
import { Contract, ContractListItem } from '@app/features/contracts/contract.model';
import { UploadSignedContractButton } from '@app/features/contracts/upload-signed-contract-button/upload-signed-contract-button';
import { ButtonAcceptAndRequestValidation } from '@app/features/contracts/validation/button-accept-and-request-validation/button-accept-and-request-validation';
import { ButtonAcceptValidationRequest } from '@app/features/contracts/validation/button-accept-validation-request/button-accept-validation-request';
import { ButtonCancelValidationRequest } from '@app/features/contracts/validation/button-cancel-validation-request/button-cancel-validation-request';
import { ButtonRefuseValidationRequest } from '@app/features/contracts/validation/button-refuse-validation-request/button-refuse-validation-request';
import { ButtonRequestValidation } from '@app/features/contracts/validation/button-request-validation/button-request-validation';
import { VALIDATION_REQUEST_TYPES } from '@app/features/contracts/validation/validation-request.model';
import { UserRole } from '@app/features/users/user.model';
import { HasRoleDirective } from '@app/shared/auth/has-role.directive';
import { YousignButton } from '@app/shared/buttons/yousign-button/yousign-button';
import { delay, Observable, tap } from 'rxjs';
import { SignatureService } from '../signature/signature-service';

/**
 * Regroupe les actions de modification d'un contrat (validation, signature).
 * Réutilisable partout où un contrat est affiché : tableau, dialogue d'embauche, ...
 */
@Component({
  selector: 'app-contract-action-buttons',
  imports: [
    CommonModule,
    ButtonCancelValidationRequest,
    ButtonRequestValidation,
    ButtonRefuseValidationRequest,
    ButtonAcceptAndRequestValidation,
    ButtonAcceptValidationRequest,
    UploadSignedContractButton,
    YousignButton,
    HasRoleDirective,
    ButtonDeleteContract,
  ],
  templateUrl: './contract-action-buttons.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./contract-action-buttons.scss'],
})
export class ContractActionButtons {
  contract = input.required<ContractListItem>();

  /** Émis quand le contrat signé a été téléversé */
  contractUpdated = output<Contract>();
  /** Émis quand une demande de signature Yousign a été envoyée */
  signatureRequested = output<void>();
  /** Émis quand l'envoi en signature a échoué : l'API a inscrit l'échec sur le contrat */
  signatureRequestFailed = output<void>();
  /** Émis avec l'identifiant du contrat quand celui-ci a été supprimé */
  contractDeleted = output<string>();

  readonly signatureService = inject(SignatureService);
  readonly snackbar = inject(MatSnackBar);

  readonly ValidationRequestTypes = VALIDATION_REQUEST_TYPES;
  readonly requestYousignSignatureRoles: UserRole[] = [
    'cdt-responsable-rh',
    'cdt-directeur-rh',
    'cdt-directeur-general',
  ];

  requestYousignSignature(): Observable<void> {
    return this.signatureService
      .requestYousignSignature(this.contract().id)
      .pipe(tap({ error: () => this.signatureRequestFailed.emit() }), delay(2000));
  }

  onYousignSignatureRequested(): void {
    this.snackbar.open('Demande de signature Yousign envoyée', undefined, {
      duration: 3000,
    });
    this.signatureRequested.emit();
  }
}
